import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Send, Eraser } from "lucide-react";
import { AiModelPicker, DEFAULT_PICK } from "./AiModelPicker";

const SUGGESTIONS = [
  "Quali prodotti vendono di più il sabato?",
  "In quali fasce orarie incassiamo di più?",
  "Com'è andato il periodo rispetto a quello precedente?",
  "Quali clienti stanno spendendo di più e quando sono venuti l'ultima volta?",
  "Qual è lo scontrino medio e come posso alzarlo?",
];

const renderLine = (line, i) => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") ? <b key={j}>{p.slice(2, -2)}</b> : p);
  if (/^\s*[-•]\s+/.test(line)) return <li key={i} className="ml-4 list-disc">{parts.map((p) => typeof p === "string" ? p.replace(/^\s*[-•]\s+/, "") : p)}</li>;
  return <p key={i} className="min-h-[0.5rem]">{parts}</p>;
};

export default function CrmAiAsk({ range, fidelityOnly, stores = [], weekdays, hours }) {
  const [pick, setPick] = useState(DEFAULT_PICK);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [items, setItems] = useState([]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/crm-ask", { ...pick, question: text, session_id: sessionId, date_from: range.date_from, date_to: range.date_to, fidelity_only: fidelityOnly, stores: stores.length ? stores.join("|") : null, weekdays: weekdays || null, hours: hours || null });
      setSessionId(data.session_id);
      setItems((l) => [...l, { q: text, a: data.answer, model: data.model, receipts: data.receipts }]);
      setQ("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-elev p-4 space-y-3" data-testid="crm-ai-ask">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-heading font-bold"><Sparkles className="h-4 w-4 text-[color:var(--paprika)]" /> Chiedi all'AI sui dati del periodo</div>
        <div className="flex items-center gap-2">
          <AiModelPicker value={pick} onChange={setPick} testid="crm-ai-pick" />
          {items.length > 0 && <Button size="sm" variant="ghost" onClick={() => { setItems([]); setSessionId(null); }} data-testid="crm-ai-clear"><Eraser className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      {items.length === 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="crm-ai-suggestions">
          {SUGGESTIONS.map((s, i) => <button key={i} type="button" disabled={busy} onClick={() => ask(s)} data-testid={`crm-ai-suggestion-${i}`} className="text-[11px] px-2.5 py-1 rounded-full border border-zinc-800 text-zinc-300 hover:border-[color:var(--paprika)] transition">{s}</button>)}
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="crm-ai-thread">
          {items.map((it, i) => (
            <div key={i} className="space-y-1.5">
              <div className="text-sm font-medium text-zinc-200" data-testid={`crm-ai-q-${i}`}>❓ {it.q}</div>
              <div className="rounded-xl bg-zinc-900/70 border border-zinc-800 p-3 text-sm text-zinc-200 space-y-0.5" data-testid={`crm-ai-a-${i}`}>
                {it.a.split("\n").map(renderLine)}
                <div className="text-[10px] text-zinc-500 pt-1">{it.model} · {it.receipts?.toLocaleString?.("it-IT")} scontrini analizzati</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} placeholder="Es. quali prodotti vendono di più il sabato pomeriggio?" className="bg-zinc-950 border-zinc-800 resize-none" data-testid="crm-ai-input"
          onKeyDown={(e) => (e.key === "Enter" && !e.shiftKey) && (e.preventDefault(), ask())} />
        <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="crm-ai-send" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] shrink-0 h-auto">
          {busy ? <Sparkles className="h-4 w-4 animate-pulse" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-[11px] text-zinc-500">L'AI risponde solo sugli aggregati del periodo e dei filtri selezionati sopra (scontrini CRM Elastic). Puoi fare domande di seguito: ricorda il contesto.</p>
    </div>
  );
}
